/**
 * Utilidades para filtrado de fechas
 * Todas las comparaciones usan zona horaria de República Dominicana (UTC-4)
 */

/**
 * Obtiene la fecha actual en RD (UTC-4) como string YYYY-MM-DD
 */
const getTodayInRD = (): string => {
  const now = new Date()
  // RD es siempre UTC-4 (no tiene horario de verano)
  const rdTime = new Date(now.getTime() - 4 * 60 * 60 * 1000)
  const year = rdTime.getUTCFullYear()
  const month = String(rdTime.getUTCMonth() + 1).padStart(2, '0')
  const day = String(rdTime.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Convierte un dateString a fecha RD (YYYY-MM-DD)
 */
const toRDDateString = (dateString: string): string => {
  try {
    let date: Date

    // Si es formato DD/MM/YYYY (formato display de RD)
    const ddmmMatch = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})/)
    if (ddmmMatch) {
      // Ya está en formato RD display, extraer la fecha directamente
      return `${ddmmMatch[3]}-${ddmmMatch[2]}-${ddmmMatch[1]}`
    }

    // Si es ISO string o similar, parsear y convertir a RD
    let dateStr = dateString.trim()
    if (
      !dateStr.endsWith('Z') &&
      !dateStr.includes('+') &&
      !dateStr.match(/\-\d{2}:\d{2}$/)
    ) {
      dateStr = dateStr + 'Z'
    }
    date = new Date(dateStr)

    if (isNaN(date.getTime())) {
      return ''
    }

    // Convertir UTC a RD (UTC-4)
    const rdTime = new Date(date.getTime() - 4 * 60 * 60 * 1000)
    const year = rdTime.getUTCFullYear()
    const month = String(rdTime.getUTCMonth() + 1).padStart(2, '0')
    const day = String(rdTime.getUTCDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  } catch {
    return ''
  }
}

/**
 * Verifica si una fecha es del día actual en zona horaria RD
 */
export const isToday = (dateString: string): boolean => {
  const todayRD = getTodayInRD()
  const dateRD = toRDDateString(dateString)
  return dateRD === todayRD
}

/**
 * Verifica si una fecha es de ayer en zona horaria RD
 */
export const isYesterday = (dateString: string): boolean => {
  try {
    const now = new Date()
    const rdTime = new Date(now.getTime() - 4 * 60 * 60 * 1000)
    rdTime.setUTCDate(rdTime.getUTCDate() - 1)
    const year = rdTime.getUTCFullYear()
    const month = String(rdTime.getUTCMonth() + 1).padStart(2, '0')
    const day = String(rdTime.getUTCDate()).padStart(2, '0')
    const yesterdayRD = `${year}-${month}-${day}`

    const dateRD = toRDDateString(dateString)
    return dateRD === yesterdayRD
  } catch {
    return false
  }
}

/**
 * Obtiene el inicio del día actual (00:00:00)
 */
export const getStartOfToday = (): Date => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

/**
 * Obtiene el fin del día actual (23:59:59)
 */
export const getEndOfToday = (): Date => {
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  return today
}

/**
 * Filtra órdenes para mostrar solo las del día actual (zona horaria RD)
 */
export const filterTodayOrders = <
  T extends {
    orderDate?: string
    orderDateISO?: string
    order_date?: string
    created_at?: string
  },
>(
  orders: T[],
): T[] => {
  const todayRD = getTodayInRD()

  return orders.filter((order) => {
    // Try ISO format first (most accurate), then fallback to display format
    const dateToCheck =
      order.orderDateISO ||
      order.order_date ||
      order.created_at ||
      order.orderDate

    if (!dateToCheck) return false

    const dateRD = toRDDateString(dateToCheck)
    return dateRD === todayRD
  })
}

/**
 * Formatea una fecha a formato legible en español
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Formatea una fecha con hora
 */
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Obtiene el rango de fechas para la semana actual
 */
export const getCurrentWeekRange = (): { start: Date; end: Date } => {
  const today = new Date()
  const dayOfWeek = today.getDay()
  const start = new Date(today)
  start.setDate(today.getDate() - dayOfWeek)
  start.setHours(0, 0, 0, 0)

  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  end.setHours(23, 59, 59, 999)

  return { start, end }
}

/**
 * Obtiene el rango de fechas para el mes actual
 */
export const getCurrentMonthRange = (): { start: Date; end: Date } => {
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), 1)
  const end = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  )

  return { start, end }
}
